import { useAsyncEffect, useRenderFile } from '@/hooks';
import { getFileExtensionFromFileName } from '@/lib/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Button, Label, Modal, Segment, Tab, TabPane, TabProps } from 'semantic-ui-react';
import DragDrop from '../DragDrop';
import fileStyles from '../FilePreviewModal/file-preview-modal.less';
import { SignatureModal } from '../SignatureModal';
import styles from './multiple-files-preview-modal.less';

const MAX_TOTAL_FILES = 20;

interface FileRecord {
  id: number;
  insertDatetime: moment.Moment;
  deleteDatetime?: moment.Moment;
  fileName?: string;
  azureBlobName?: string;
  label?: string;
}

interface MultipleFilesPreviewModalProps {
  visible: boolean;
  formTitle: string;
  files: FileRecord[];
  archivedFiles?: FileRecord[];
  signature?: string;
  fetchUrl: (fileId: number) => Promise<string | undefined>;
  fetchBlob: (fileId: number) => Promise<Blob>;
  handleDownload: (fileId: number, fileName?: string) => Promise<void>;
  handleArchive?: (fileId: number) => Promise<void>;
  handleRestore?: (fileId: number) => Promise<void>;
  handleUpload?: (files: File[]) => Promise<void>;
  onClose: () => void;
}

const acceptedFileTypes = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

export const MultipleFilesPreviewModal = ({
  visible,
  onClose,
  formTitle,
  files,
  archivedFiles = [],
  signature,
  fetchUrl,
  fetchBlob,
  handleDownload,
  handleArchive,
  handleRestore,
  handleUpload,
}: MultipleFilesPreviewModalProps) => {
  const [activeHorizontalTab, setActiveHorizontalTab] = useState(0);
  const [selectedVerticalTabIndex, setSelectedVerticalTabIndex] = useState(0);
  const [fileBlobs, setFileBlobs] = useState<Record<number, Blob>>({});
  const [fileUrls, setFileUrls] = useState<Record<number, string>>({});
  const [fetchingFiles, setFetchingFiles] = useState<Set<number>>(new Set());
  const [shouldShowSignatureModal, setShouldShowSignatureModal] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [dragDropHasError, setDragDropHasError] = useState(false);

  // Determine which files to show based on active horizontal tab
  const currentFiles = activeHorizontalTab === 0 ? files : archivedFiles;
  const isArchiveTab = activeHorizontalTab === 1;

  // Only show upload functionality on active files tab
  const showUpload = !isArchiveTab && handleUpload;
  const canAction = selectedVerticalTabIndex > 0 || (selectedVerticalTabIndex === 0 && !showUpload);

  // Set initial tab selection when modal opens
  useAsyncEffect(async () => {
    if (visible && showUpload && currentFiles.length > 0) {
      setSelectedVerticalTabIndex(1); // Select first file tab when upload is enabled
    } else if (visible && !showUpload && currentFiles.length > 0) {
      setSelectedVerticalTabIndex(0); // Select first file tab when upload is disabled
    }
  }, [visible, showUpload, currentFiles.length, activeHorizontalTab]);

  // Load the selected file when modal opens or when selected tab changes
  useAsyncEffect(async () => {
    const fileIndex = showUpload ? selectedVerticalTabIndex - 1 : selectedVerticalTabIndex;
    if (visible && currentFiles.length > 0 && currentFiles[fileIndex]) {
      await fetchFileData(currentFiles[fileIndex].id);
    }
  }, [visible, currentFiles, selectedVerticalTabIndex, showUpload]);

  const fileIndex = showUpload ? selectedVerticalTabIndex - 1 : selectedVerticalTabIndex;
  const selectedFile = currentFiles[fileIndex];
  const selectedFileId = selectedFile?.id;
  const selectedFileBlob = selectedFileId ? fileBlobs[selectedFileId] : undefined;
  const selectedFileUrl = selectedFileId ? fileUrls[selectedFileId] : undefined;

  const fileExtension = selectedFile?.fileName
    ? getFileExtensionFromFileName(selectedFile.fileName)
    : '';

  const { renderedFile, isWord, isExcel, isPDF, isImage } = useRenderFile({
    fileExtension,
    fileLocation: selectedFileUrl,
    blob: selectedFileBlob,
    fileName: selectedFile?.fileName || '',
    azureBlobName: selectedFile?.azureBlobName || '',
  });

  const fetchFileData = async (fileId: number) => {
    // Skip if already fetched
    if (fileBlobs[fileId]) {
      return;
    }

    // Mark as fetching
    setFetchingFiles((prev) => new Set(prev).add(fileId));

    try {
      // For PDFs and other sensitive files, prioritize blob fetching
      const allFiles = [...files, ...archivedFiles];
      const file = allFiles.find((f) => f.id === fileId);
      const fileExt = file?.fileName ? getFileExtensionFromFileName(file.fileName) : '';

      if (
        fileExt === 'pdf' ||
        fileExt === 'doc' ||
        fileExt === 'docx' ||
        fileExt === 'xls' ||
        fileExt === 'xlsx'
      ) {
        // For PDFs and documents, only fetch blob
        const blob = await fetchBlob(fileId);
        setFileBlobs((prev) => ({ ...prev, [fileId]: blob }));
      } else {
        // For images and other files, try blob first, then URL as fallback
        const [blobResult, urlResult] = await Promise.allSettled([
          fetchBlob(fileId),
          fetchUrl(fileId),
        ]);

        // Prioritize blob
        if (blobResult.status === 'fulfilled') {
          setFileBlobs((prev) => ({ ...prev, [fileId]: blobResult.value }));
        } else if (urlResult.status === 'fulfilled' && urlResult.value) {
          // Only use URL for images as fallback
          setFileUrls((prev) => ({ ...prev, [fileId]: urlResult.value! }));
        }
      }
    } finally {
      // Remove from fetching set
      setFetchingFiles((prev) => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handleFileDrop = (droppedFiles: File[]) => {
    setFilesToUpload((prevFiles) => [...prevFiles, ...droppedFiles]);
  };

  const handleUploadFiles = async () => {
    if (!handleUpload || filesToUpload.length === 0) {
      return;
    }

    setUploadingFiles(true);
    try {
      await handleUpload(filesToUpload);
      setFilesToUpload([]); // Clear files after successful upload
    } catch (error) {
      console.error('Error uploading files:', error);
      // You could add error handling here (e.g., show a toast notification)
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedFile && selectedFileId) {
      await handleDownload(selectedFileId, selectedFile.fileName);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedFile && selectedFileId && handleArchive) {
      await handleArchive?.(selectedFile.id);

      // Adjust tab index after deletion
      const addFileTabOffset = showUpload ? 1 : 0;

      // If we deleted the last file and there are still files remaining
      if (fileIndex >= currentFiles.length - 1 && currentFiles.length > 1) {
        return setSelectedVerticalTabIndex(currentFiles.length - 2 + addFileTabOffset); // Move to the new last file
      }

      // If we deleted the only file, move to the "Add File" tab if it exists, otherwise stay at 0
      if (currentFiles.length === 1 && showUpload) {
        setSelectedVerticalTabIndex(0); // Move to "Add File" tab
      }
    }
  };

  const handleRestoreSelected = async () => {
    if (selectedFile && selectedFileId && handleRestore) {
      await handleRestore?.(selectedFile.id);
    }
  };

  const getRenderedFile = () => {
    if (!selectedFile) {
      return <div className={styles.centered_message}>No file selected...</div>;
    }

    if (selectedFileId && fetchingFiles.has(selectedFileId)) {
      return <div className={styles.centered_message}>Loading file...</div>;
    }

    // If we have file data but no rendered file yet, show loading
    if ((selectedFileBlob || selectedFileUrl) && renderedFile === undefined) {
      return <div className={styles.centered_message}>Processing file...</div>;
    }

    if (renderedFile) {
      return (
        <div
          className={classNames(fileStyles.file_preview_container, styles.file_preview_wrapper, {
            [fileStyles.image]: isImage,
            [fileStyles.document]: isWord || isExcel,
            [styles.word_excel_height]: isWord || isExcel,
            [styles.pdf_height]: isPDF,
          })}
        >
          {(isWord || isExcel) && (
            <div className={styles.document_instructions}>
              Click the download button below to download this file.
              <br />
              <br />
              {isWord &&
                'You will need a program that can open Microsoft Word documents in order to open this file.'}
              {isExcel &&
                'You will need a program that can open Microsoft Excel documents in order to open this file.'}
            </div>
          )}

          {!(isWord || isExcel) && renderedFile}
        </div>
      );
    }

    // Show more specific messages based on what we have
    if (!selectedFileBlob && !selectedFileUrl) {
      return <div className={styles.centered_message}>Loading file data...</div>;
    }

    if (!fileExtension) {
      return (
        <div className={styles.centered_message}>
          File type could not be determined from filename: {selectedFile.fileName}
        </div>
      );
    }

    return (
      <div className={styles.centered_message}>
        Unable to preview {fileExtension.toUpperCase()} files.
        <br />
        <small>File: {selectedFile.fileName}</small>
      </div>
    );
  };

  const generateVerticalTabPanes = (
    fileList: FileRecord[],
    allowUpload: boolean,
  ): TabProps['panes'] => [
    ...(allowUpload
      ? [
          {
            menuItem: {
              key: 'add-file',
              content: 'Add Files',
              icon: 'plus',
            },
            render: () => (
              <TabPane className="add_file_tab">
                <DragDrop
                  text={uploadingFiles ? 'Uploading files...' : 'Drag and drop files'}
                  dragText="Drop your files here!"
                  accept={acceptedFileTypes}
                  allowReplace
                  multiple
                  maxFiles={MAX_TOTAL_FILES}
                  files={filesToUpload}
                  height={540}
                  onFileDrop={handleFileDrop}
                  onClear={() => setFilesToUpload([])}
                  onFileDelete={(index) => {
                    const newFiles = [...filesToUpload];
                    newFiles.splice(index, 1);
                    setFilesToUpload(newFiles);
                  }}
                  onError={setDragDropHasError}
                />

                <div className={styles.upload_container}>
                  <div className={styles.upload_buttons}>
                    <Button
                      color="green"
                      loading={uploadingFiles}
                      disabled={
                        filesToUpload.length === 0 ||
                        uploadingFiles ||
                        dragDropHasError ||
                        files.length + filesToUpload.length > MAX_TOTAL_FILES
                      }
                      onClick={handleUploadFiles}
                    >
                      <FontAwesomeIcon
                        icon={['fas', 'upload']}
                        className={styles.icon_margin_right}
                      />
                      Upload {filesToUpload.length} file{filesToUpload.length > 1 ? 's' : ''}
                    </Button>
                  </div>
                  {files.length + filesToUpload.length > MAX_TOTAL_FILES && (
                    <div className={styles.error_message}>
                      <FontAwesomeIcon
                        icon={['fas', 'exclamation-triangle']}
                        className={styles.icon_margin_right_small}
                      />
                      Cannot upload: Total files would exceed maximum limit of{' '}
                      <strong>{MAX_TOTAL_FILES}</strong> files.
                      <br />
                      You have <strong>{files.length}</strong> files already uploaded and{' '}
                      <strong>{filesToUpload.length}</strong> ready to upload.
                    </div>
                  )}
                </div>
              </TabPane>
            ),
          },
        ]
      : []),
    ...fileList.map(({ id, insertDatetime, deleteDatetime, fileName, label }, index) => ({
      menuItem: {
        key: id,
        content: (
          <div className={styles.tab_menu_item} title={fileName || `File ${id}`}>
            {fileName || `File ${id}`}
          </div>
        ),
      },
      render: () => (
        <TabPane>
          <Segment className={styles.file_segment}>
            <div className={styles.file_header}>
              <div className={styles.file_title}>{fileName ?? 'Uploaded File'}</div>

              {label && (
                <Label color="blue" className={styles.file_label}>
                  {label}
                </Label>
              )}
            </div>

            <div className={styles.file_uploaded_at}>
              Uploaded on {insertDatetime.format('MMMM Do [at] h:mmA')}
              {deleteDatetime && (
                <>
                  {' '}
                  <span className={styles.deleted_file_label}>
                    &bull; Archived on {deleteDatetime.format('MMMM Do [at] h:mmA')}
                  </span>
                </>
              )}
            </div>

            <div className={styles.file_content}>
              {index === fileIndex ? getRenderedFile() : <div>Loading...</div>}
            </div>
          </Segment>
        </TabPane>
      ),
    })),
  ];

  const horizontalTabPanes: TabProps['panes'] = [
    {
      menuItem: {
        key: 'current-files',
        content: `Current Files (${files.length})`,
      },
      render: () => (
        <TabPane>
          {files.length === 0 && !handleUpload ? (
            <div className={styles.centered_message}>No files uploaded...</div>
          ) : (
            <Tab
              activeIndex={selectedVerticalTabIndex}
              className="vertical_tab"
              menu={{ fluid: true, vertical: true, tabular: true }}
              panes={generateVerticalTabPanes(files, !!handleUpload)}
              onTabChange={async (_, { activeIndex }) => {
                const newIndex =
                  typeof activeIndex === 'string' ? Number(activeIndex) : activeIndex;

                if (newIndex !== undefined) {
                  setSelectedVerticalTabIndex(newIndex);
                  const fileIndex = showUpload ? newIndex - 1 : newIndex;
                  const selectedFile = files[fileIndex];

                  if (selectedFile) {
                    await fetchFileData(selectedFile.id);
                  }
                }
              }}
            />
          )}
        </TabPane>
      ),
    },
    {
      menuItem: {
        key: 'archived-files',
        content: `Archived Files (${archivedFiles.length})`,
      },
      render: () => (
        <TabPane>
          {archivedFiles.length === 0 ? (
            <div className={styles.centered_message}>No archived files...</div>
          ) : (
            <Tab
              activeIndex={selectedVerticalTabIndex}
              className="vertical_tab"
              menu={{ fluid: true, vertical: true, tabular: true }}
              panes={generateVerticalTabPanes(archivedFiles, false)}
              onTabChange={async (_, { activeIndex }) => {
                const newIndex =
                  typeof activeIndex === 'string' ? Number(activeIndex) : activeIndex;

                if (newIndex !== undefined) {
                  setSelectedVerticalTabIndex(newIndex);
                  const selectedFile = archivedFiles[newIndex];

                  if (selectedFile) {
                    await fetchFileData(selectedFile.id);
                  }
                }
              }}
            />
          )}
        </TabPane>
      ),
    },
  ];

  return (
    <>
      <Modal size="large" open={visible}>
        <Modal.Header>
          Files for {formTitle}
          <FontAwesomeIcon
            icon={['fas', 'times']}
            className={styles.modal_close_icon}
            onClick={onClose}
          />
        </Modal.Header>

        <Modal.Content scrolling className={styles.modal_content}>
          <div className="tab_menu">
            <Tab
              activeIndex={activeHorizontalTab}
              menu={{ text: true }}
              panes={horizontalTabPanes}
              onTabChange={(_, { activeIndex }) => {
                const newIndex =
                  typeof activeIndex === 'string' ? Number(activeIndex) : activeIndex;
                if (newIndex !== undefined) {
                  setActiveHorizontalTab(newIndex);
                  setSelectedVerticalTabIndex(0); // Reset vertical tab when switching horizontal tabs
                }
              }}
            />
          </div>
        </Modal.Content>

        <Modal.Actions>
          <div>
            <Button
              color="green"
              className={styles.modal_button}
              disabled={!selectedFile?.fileName || !canAction}
              onClick={() => handleDownloadSelected()}
            >
              <FontAwesomeIcon icon={['fas', 'download']} className={styles.icon_margin_right} />
              {selectedFile?.fileName && canAction ? 'Download File' : 'No File'}
            </Button>
            <Button
              color="blue"
              className={styles.modal_button}
              disabled={!signature}
              onClick={() => setShouldShowSignatureModal(true)}
            >
              <FontAwesomeIcon
                icon={['fas', 'file-signature']}
                className={styles.icon_margin_right}
              />
              {signature ? 'View Signature' : 'No Signature'}
            </Button>
          </div>

          {handleArchive && !isArchiveTab && (
            <Button
              color="red"
              className={styles.modal_button}
              disabled={!selectedFile?.fileName || !canAction}
              onClick={handleDeleteSelected}
            >
              <FontAwesomeIcon icon={['fas', 'archive']} className={styles.icon_margin_right} />
              {selectedFile?.fileName && canAction ? 'Archive File' : 'No File'}
            </Button>
          )}

          {handleRestore && isArchiveTab && (
            <Button
              color="blue"
              className={styles.modal_button}
              disabled={!selectedFile?.fileName || !canAction}
              onClick={handleRestoreSelected}
            >
              <FontAwesomeIcon
                icon={['fas', 'trash-restore']}
                className={styles.icon_margin_right}
              />
              {selectedFile?.fileName && canAction ? 'Restore File' : 'No File'}
            </Button>
          )}
        </Modal.Actions>
      </Modal>

      {shouldShowSignatureModal && signature && (
        <SignatureModal
          visible={shouldShowSignatureModal}
          signature={signature}
          onClose={() => setShouldShowSignatureModal(false)}
        />
      )}
    </>
  );
};
